import {useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {useFormik} from 'formik'
import {useAuth} from '../../../modules/auth'
import {useDropzone} from 'react-dropzone'
import styles from '../../ticketing/TicketingStyles/TicketingStyles.module.scss'
import { createTickets } from '../../ticketing/TicketingAPI'
import Swal from 'sweetalert2'
import { CreateTenantTicketDataType, CreateTenantTicketFormikValuesDataType } from '../RentedPropertiesDataType'
import * as Yup from 'yup'

const modalsRoot = document.getElementById('root-modals') || document.body

const initialValues = {
    ticket_title: '',
    ticket_description: '',
}
const validationSchema = Yup.object({
    ticket_title: Yup.string()
      .min(3, 'Minimum 3 characters')
      .max(150, 'Maximum 150 characters')
      .required('Ticket title is required'),
    ticket_description: Yup.string()
      .min(3, 'Minimum 3 characters')
      .max(1000, 'Maximum 1000 characters')
      .required('Ticket description is required'),
  })
  
const CreateTenantTicket = ({
  showCreateTicketModal,
  handleClose,
  property_uuid
}: CreateTenantTicketDataType) => {
  const {currentUser} = useAuth()
  const [loading, setLoading] = useState<boolean>(false)

  // formik object for create ticket form
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => handleSubmit(values, acceptedFiles),
  })

  // function to create the ticket
  const handleSubmit = async (values: CreateTenantTicketFormikValuesDataType, acceptedFiles: File[]) => {
    const promises = acceptedFiles.map((file) => {
      return new Promise<{file: string}>((resolve) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          const base64String = (event.target!.result as string).split(',')[1]
          resolve({file: base64String})
        }
        reader.readAsDataURL(file)
      })
    })
    const convertedFiles = await Promise.all(promises)
    if (currentUser?.account_uuid) {
      setLoading(true)
      try {
        const {data: response} = await createTickets(
          1,
          property_uuid,
          "",
          values.ticket_title,
          values.ticket_description,
          convertedFiles
        )
        if (response.code === '200') {
          handleClose()
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: 'The ticket has been created successfully.',
            allowOutsideClick: true,
          })
        } else {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
            text: response.msg_details,
            allowOutsideClick: true,
          })
        }
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
          allowOutsideClick: true,
        })
      } finally {
        setLoading(false)
      }
    }
  }

  // dropzone code start
  const {acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive} = useDropzone({
    maxFiles: 3,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'application/pdf': [],
    },
  })

  const acceptedFileItems = acceptedFiles.map((file) => {
    return (
      // @ts-ignore
      <li key={file.path}>
        {
          // @ts-ignore
          file.path
        }{' '}
        - {file.size} bytes
      </li>
    )
  })

  const fileRejectionItems = fileRejections.map(({file, errors}) => (
    // @ts-ignore
    <li key={file.path}>
      {
        // @ts-ignore
        file.path
      }{' '}
      - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ))
  // dropzone code ends
  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-700px'
      show={showCreateTicketModal}
      onHide={handleClose}
      backdrop={true}
    >
      <div className='modal-header py-3'>
        {/* begin::Heading */}
        <h2>Create Ticket</h2>
        {/* end::Heading */}
        {/* begin::Close */}
        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
          <i className='bi bi-x-lg' onClick={handleClose}></i>
        </div>
        {/* end::Close */}
      </div>

      <div className='modal-body px-10 py-3'>
        {/*begin::Form */}
        <form
          onSubmit={formik.handleSubmit}
          className='d-flex flex-column justify-content-center px-5'
        >
          {/*begin::Form Field Ticket Title */}
          <div className='fv-row'>
            <label className='d-flex fs-5 fw-semibold pb-1'>
              <span className='required'>Title</span>
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title='Enter ticket title'
              ></i>
            </label>
            <input
              type='text'
              className='form-control form-control-lg form-control-solid'
              placeholder='Enter ticket title'
              {...formik.getFieldProps('ticket_title')}
              name='ticket_title'
            />
            <div className='h-20px'>
              {formik.touched.ticket_title && formik.errors.ticket_title && (
                <div className='fv-help-block color-red'>{formik.errors.ticket_title}</div>
              )}
            </div>
          </div>
          {/*end::Form Field Ticket Title */}

          {/*begin::Form Field Ticket Description */}
          <div className='fv-row'>
            <label className='d-flex fs-5 fw-semibold pb-1'>
              <span className='required'>Description</span>
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title='Enter ticket description'
              ></i>
            </label>
            <textarea
              className='form-control form-control-lg form-control-solid'
              {...formik.getFieldProps('ticket_description')}
              name='ticket_description'
              placeholder='Enter ticket description'
            />
            <div className='h-20px'>
              {formik.touched.ticket_description && formik.errors.ticket_description && (
                <div className='fv-help-block color-red'>{formik.errors.ticket_description}</div>
              )}
            </div>
          </div>
          {/*end::Form Field Ticket Description */}

          {/*begin::Form Field Document upload */}
          <div className='fv-row'>
            <label className='d-flex fs-5 fw-semibold pb-1'>
              <span className=''>Document upload</span>
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title='Upload documents related to the ticket'
              ></i>
            </label>
            <div className='d-flex justify-content-between'>
              {/*begin::Dropzone field */}
              <div
                className={`border border-2 rounded border-dashed row p-1 bg-white mx-1 text-center ${
                  acceptedFileItems.length > 0 || fileRejectionItems.length > 0
                    ? styles.max_w_49
                    : 'w-100'
                }`}
              >
                <section className='border border-2 rounded border-primary border-dashed h-100 symbol bg-light-primary'>
                  <div
                    {...getRootProps({
                      className:
                        'py-2 d-flex flex-column justify-content-center align-items-center',
                    })}
                  >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <p className='mb-1'>Drop the files here...</p>
                    ) : (
                      <p className='mb-1'>Drag and drop files here, or click to select files</p>
                    )}
                    <i className='bi bi-file-earmark-arrow-up-fill fs-3x btn btn-active-color-primary py-0 mb-1'></i>
                    <span className='text-primary fs-7'>
                      Type: *.jpeg, *.jpg, *.png, *.pdf only
                    </span>
                    <span className='text-primary fs-7'>Max document:3</span>
                  </div>
                </section>
              </div>

              <aside
                className={` ${
                  acceptedFileItems.length > 0 || fileRejectionItems.length > 0
                    ? `${styles.max_w_49} px-2 me-2`
                    : 'w-0'
                }`}
              >
                {acceptedFileItems.length > 0 && (
                  <div className='pt-2'>
                    <h4>Document is ready to upload</h4>
                    <ul>{acceptedFileItems}</ul>
                  </div>
                )}
                {fileRejectionItems.length > 0 && (
                  <div className='pt-2'>
                    <span className='fv-help-block color-red'>
                      Upload only three files or select files with allowed formats only
                    </span>
                    {/* <ul>{fileRejectionItems}</ul> */}
                  </div>
                )}
              </aside>
              {/*end::Dropzone field */}
            </div>
          </div>
          {/*end::Form Field Document upload */}
          {/*begin::Form Field Update button */}
          <button
            type='submit'
            className='btn btn-lg btn-primary mx-auto mt-2 py-3'
            disabled={loading}
          >
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Processing...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            ) : (
              'Submit'
            )}
          </button>
          {/*end::Form Field Update button */}
        </form>
        {/*end::Form */}
      </div>
      {/* end::Stepper */}
      {/* </div> */}
    </Modal>,
    modalsRoot
  )
}

export default CreateTenantTicket
